import React, { useEffect, useState } from "react";
import { Col, Input, Row, Select, Form, DatePicker } from "antd";
import { ReactComponent as SaveIcon } from "../../../assets/save-icon.svg";
import { ReactComponent as EditIcon } from "../../../assets/edit-icon.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  createLab,
  setLabDetailsIsEdit,
  updateLab,
  fetchLabUsers,
  fetchWarehouses,
  fetchAllLabs,
} from "../redux/actions";
import SpinnerComponent from "../../../components/spinner";
import { isValidNumber } from "../../../globalUtils/axiosConfig";
import moment from "moment";
import AttachmentsSection from "./attachmentsSection";
import StockNotificationRecipientsSection from "./stockNotificationRecipientsSection";
import AssignedUsersSection from "./assignedUsersSection";
import { size } from "lodash";
import { useInternalMessage } from "antd/es/message/useMessage";

const LabDetailsSection = ({ isLabUpdate, canUpdate }) => {
  const [documents, setDocuments] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [utils, setUtils] = useState({});
  const [currentLab, setCurrentLab] = useState({});
  const [isLabSelected, setIsLabSelected] = useState(false);
  const [labList, setLabList] = useState([]);

  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { TextArea } = Input;
  const { Option } = Select;

  const {
    createLabLoading,
    currentLabObj,
    labDetailsIsEdit,
    utilsData,
    assignedUsers,
    fetchLabUsersLoading,
    warehouses,
    fetchWarehousesLoading,
    allLabs,
  } = useSelector((state) => state.labsSetupReducer);

  const {
    allLabStatuses
  } = useSelector(
    (state) => state.labStatusesReducer
  );

  // Get the current user to check permissions
  const { user } = useSelector((state) => state.authReducer);

  useEffect(() => {
    if (currentLabObj) {
      const { endDate, startDate, attachments } = currentLabObj;

      // Prepare a version of lab data where location is an ID
      let labDataForForm = { ...currentLabObj };
      if (labDataForForm.location && typeof labDataForForm.location === 'object') {
        labDataForForm.location = labDataForForm.location.id; // Ensure location is an ID
      } // If it's already a primitive ID, it's fine.

      setCurrentLab(labDataForForm); // Use this for initialValues via currentLab state

      form.setFieldsValue({
        ...labDataForForm, // This now also has location as an ID
        endDate: endDate ? moment(endDate) : undefined, // Handle potentially undefined dates
        startDate: startDate ? moment(startDate) : undefined,
      });

      if (currentLabObj.id) {
        dispatch(fetchLabUsers(currentLabObj.id));
      }

      if (attachments && attachments?.length >= 0) {
        let updatedImages = attachments.map((img) => {
          let { id, path, originalName } = img;
          console.log("name", originalName);

          if (!originalName && path) {
            const urlParts = path.split("/");
            originalName = urlParts[urlParts.length - 1].split("?")[0];
            try {
              originalName = decodeURIComponent(originalName);
            } catch (e) {
              console.error("Error decoding URI:", e);
            }

            originalName = originalName.replaceAll(/[^a-zA-Z0-9.-\s]/g, " ").trim();
          }

          return { key: id, image: path, isLocal: false, name: originalName };
        });

        setDocuments(updatedImages);
      }
      setUtils(utilsData);
    } else {
      setCurrentLab({});
      form.resetFields();
    }
  }, [currentLabObj, form, dispatch, utilsData]);

  useEffect(() => {
    setLoading(createLabLoading);
    // if (createLabLoading) {
    //   setIsUserImgSelected(false);
    // }
  }, [createLabLoading]);

  useEffect(() => {
    dispatch(fetchWarehouses());
    dispatch(fetchAllLabs({ search: {}, perPage: 1000 }));
  }, [dispatch]);

  useEffect(() => {
    setLabList(warehouses);
  }, [warehouses]);

  useEffect(() => {
    if (!isLabUpdate) {
      // For new labs, start in edit mode
      setIsEdit(true);
      dispatch(setLabDetailsIsEdit(true));
    } else {
      // For existing labs, start in view mode
      setIsEdit(false);
      dispatch(setLabDetailsIsEdit(false));
    }
  }, [isLabUpdate, dispatch]);

  const handleLabSelect = (labId) => {
    const selectedLab = labList.find(lab => lab.id === labId);
    if (selectedLab) {
      form.setFieldsValue({
        labName: selectedLab.labName,
        wareHouseId: selectedLab.wareHouseId,
        branchCode: selectedLab.branchCode,
        activeOnOmni: selectedLab.activeOnOmni ? 'Yes' : 'No',
        contactName: selectedLab.contactName,
        contactNumber: selectedLab.contactNumber,
        contactEmail: selectedLab.contactEmail,
        projectAddress: selectedLab.projectAddress
      });
      setIsLabSelected(true);
    } else {
      setIsLabSelected(false);
    }
  }

  const onChange = (value) => {
    // console.log(`selected ${value}`);
  };

  const onFinish = async (values) => {
    const { ...otherValues } = values;

    // Convert activeOnOmni from string to boolean if needed
    if (typeof otherValues.activeOnOmni === 'string') {
      otherValues.activeOnOmni = otherValues.activeOnOmni === 'Yes';
    }

    const payload = {
      ...otherValues,
      ...(documents && { documents }), // Add documents if they exist
    };

    if (isLabUpdate) {
      payload.id = currentLabObj.id; // Add id for update
      dispatch(updateLab(payload));
    } else {
      dispatch(createLab(payload));
    }
  };

  const formItemLayout = {
    labelCol: {
      span: 24,
    },
    wrapperCol: {
      span: 24,
    },
  };

  const handleSave = () => {
    form.submit();
    // After form submission, switch back to view mode
    setIsEdit(false);
    dispatch(setLabDetailsIsEdit(false));
  };

  const handleEdit = () => {
    // Switch to edit mode
    setIsEdit(true);
    dispatch(setLabDetailsIsEdit(true));
  };

  // const capitalizeFirstLetter = (str) =>
  //   str && str.charAt(0).toUpperCase() + str.slice(1);

  return (
    <>
      <Row>
        <Col span={24} className="heading-col">
          <Row align="middle" justify="space-between">
            <Col>
              <div>
                <span className="heading-span">Lab Details</span>
              </div>
            </Col>
            <Col>
              {isEdit ? (
                loading ? (
                  <SpinnerComponent fontSize={14} />
                ) : (
                  <SaveIcon width={16} height={16} fill="#ed017f" onClick={handleSave} />
                )
              ) : (
                canUpdate && (
                  <EditIcon
                    width={16}
                    fill="#ed017f"
                    height={16}
                    onClick={handleEdit}
                  />
                )
              )}
            </Col>
          </Row>
        </Col>
        <Col
          span={24}
          className={`user-details-section lab-details-section ${!isEdit ? "view-mode" : "edit-mode"
            }`}
        >
          <Row justify="center">
            <Col md={24} lg={23} xl={20} xxl={16}>
              <Form
                {...formItemLayout}
                form={form}
                name="user-details"
                onFinish={onFinish}
                initialValues={currentLab}
                scrollToFirstError
                labelWrap={true}
                layout="horizontal"
                className="user-detail-form"
                requiredMark={false}
              >
                <Row gutter={[20, 10]}>
                  <Col span={16}>
                    <Form.Item
                      rules={[
                        {
                          required: true,
                          message: "Required!",
                        },
                      ]}
                      name="labName"
                      label="LAB NAME"
                    >
                      <Select
                        style={{
                          width: '100%',
                        }}
                        readOnly={true}
                        disabled={isLabUpdate || fetchWarehousesLoading} // Disable when editing existing lab or loading warehouses
                        className="department-select"
                        dropdownClassName="select-dropdown-custom"
                        getPopupContainer={() =>
                          document.getElementsByClassName(
                            "ant-layout-content layout-content site-layout-background custom-textarea"
                          )[0]
                        }
                        showSearch
                        placeholder={fetchWarehousesLoading ? "Loading labs..." : "Select Lab Name"}
                        optionFilterProp="label"
                        onChange={handleLabSelect}
                        filterOption={(input, option) =>
                          (option.label ?? "")
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                        loading={fetchWarehousesLoading}
                      >
                        {labList.map(lab => {
                          const isAlreadyTaken = !isLabUpdate && allLabs?.some(existingLab => existingLab.wareHouseId === lab.id);
                          return (
                            <Option key={lab.id} value={lab.id} label={lab.labName} disabled={isAlreadyTaken}>
                              {lab.labName}
                            </Option>
                          );
                        })}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col>
                    <Form.Item name="wareHouseId" noStyle />
                  </Col>
                  <Col md={8} lg={8} xl={8} xxl={8}>
                    <Form.Item
                      rules={[
                        {
                          required: true,
                          message: "Required!",
                        },
                      ]}
                      name="branchCode"
                      label="Branch Code"
                    >
                      <Input
                        readOnly={true}
                        disabled={!isEdit || !canUpdate || !isLabSelected}
                        style={{ backgroundColor: isLabSelected ? "#fff" : "#f0f0f0" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col md={8} lg={8} xl={8} xxl={8}>
                    <Form.Item
                      rules={[
                        {
                          required: true,
                          message: "Required!",
                        },
                      ]}
                      name="activeOnOmni"
                      label="Active On Omni"
                    >
                      <Input
                        readOnly={true}
                        disabled={!isEdit || !canUpdate || !isLabSelected}
                        style={{ backgroundColor: isLabSelected ? "#fff" : "#f0f0f0" }}
                      />
                    </Form.Item>
                  </Col>
                  {/* <Col md={8} lg={8} xl={8} xxl={8}>
                    <Form.Item
                      rules={[
                        {
                          required: true,
                          message: "Required!",
                        },
                        {
                          type: 'email',
                          message: 'Please enter a valid email!',
                        },
                      ]}
                      name="contactEmail"
                      label="Contact Email"
                    >
                      <Input
                        readOnly={true}
                        disabled={!isEdit || !canUpdate || !isLabSelected}
                        style={{ backgroundColor: isLabSelected ? "#fff" : "#f0f0f0" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      rules={[
                        {
                          required: true,
                          message: "Required!",
                        },
                      ]}
                      name="physicalAddress"
                      label="Physical Address"
                    >
                      <Input
                        readOnly={true}
                        disabled={!isEdit || !canUpdate || !isLabSelected}
                        style={{ backgroundColor: isLabSelected ? "#fff" : "#f0f0f0" }}
                      />
                    </Form.Item>
                  </Col> */}
                  <Col span={10}>
                    <Form.Item
                      rules={[
                        {
                          required: true,
                          message: "Required!",
                        },
                      ]}
                      name="labStatusId"
                      label="Lab Status"
                    >
                      <Select
                        style={{
                          width: "100%",
                        }}
                        disabled={!isEdit || !canUpdate && !isLabSelected}
                        readOnly={true}
                        className="department-select"
                        dropdownClassName="select-dropdown-custom"
                        getPopupContainer={() =>
                          document.getElementsByClassName(
                            "ant-layout-content layout-content site-layout-background custom-textarea"
                          )[0]
                        }
                        showSearch
                        placeholder="Select Lab Status"
                        optionFilterProp="children"
                        onChange={onChange}
                        // onSearch={onSearch}
                        filterOption={(input, option) =>
                          option.children
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                      >
                        {allLabStatuses.map(({ id, statusName }) => {
                          return (
                            <Option key={id} value={id}>
                              {statusName}
                            </Option>
                          );
                        })}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      name="notes"
                      label="Notes / Comments"
                    >
                      <TextArea
                        className="notes"
                        rows={7}
                        readOnly={loading}
                        disabled={!isEdit || !canUpdate && !isLabSelected}
                        placeholder="Insert Notes or Comments"
                      />
                    </Form.Item>
                    <AttachmentsSection
                      isEdit={isEdit}
                      loading={loading}
                      documents={documents}
                      setDocuments={setDocuments}
                      currentLab={currentLab}
                      disabled={!isLabUpdate && !isLabSelected}
                    />
                  </Col>
                </Row>
              </Form>
            </Col>
          </Row>
        </Col>
      </Row>
      <div className="empty-div"></div>

      <AssignedUsersSection
        assignedUsers={assignedUsers}
        loading={fetchLabUsersLoading}
      />

      {/* Stock Notification Recipients Section */}
      <StockNotificationRecipientsSection
        isLabUpdate={isLabUpdate}
        canUpdate={canUpdate}
        labId={currentLabObj?.id}
        initialRecipients={currentLabObj?.stockNotificationsRecipients || []}
        isLabSelected={isLabSelected}
      />
    </>
  );
};

export default LabDetailsSection;

